import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import Speedometer from "../components/Speedometer";

function Dashboard() {
  const { darkMode } = useTheme();
  const { token } = useAuth();
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [recentAlerts, setRecentAlerts] = useState([]);
  const [recentChecks, setRecentChecks] = useState([]);
  const [checksFilter, setChecksFilter] = useState('errors'); // 'all' or 'errors'
  const [sfmcStats, setSfmcStats] = useState(null);
  const [loadingSfmcStats, setLoadingSfmcStats] = useState(false);
  const [selectedAccountId, setSelectedAccountId] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editingMaxContacts, setEditingMaxContacts] = useState(false);
  const [maxContactsValue, setMaxContactsValue] = useState('');

  const fetchDashboardData = useCallback(async () => {
    try {
      const summaryRes = await axios.get('/api/dashboard/summary');
      setSummary(summaryRes.data);
      setRecentAlerts(summaryRes.data.recentAlerts || []);
      setRecentChecks(summaryRes.data.recentChecks || []);
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message || 'Unknown error';
      setError(`Failed to load dashboard data: ${errorMsg}`);
    } finally {
      setLoading(false);
    }
  }, [token]);

  const fetchSfmcStats = useCallback(async (accountId = null) => {
    setLoadingSfmcStats(true);
    try {
      const url = accountId
        ? `/api/dashboard/sfmc-stats?accountId=${accountId}`
        : '/api/dashboard/sfmc-stats';
      const statsRes = await axios.get(url);
      setSfmcStats(statsRes.data);
      // Set the selected account ID if not already set
      if (!selectedAccountId && statsRes.data.currentAccount?.id) {
        setSelectedAccountId(statsRes.data.currentAccount.id);
      }
    } catch (err) {
      console.error('Failed to load SFMC stats:', err);
    } finally {
      setLoadingSfmcStats(false);
    }
  }, []);

  const saveMaxContacts = async (value) => {
    try {
      const numValue = parseInt(value.replace(/,/g, ''), 10) || 0;
      await axios.put('/api/dashboard/profile', { maxContacts: numValue });
      // Refresh dashboard data to get updated value
      fetchDashboardData();
    } catch (err) {
      console.error('Failed to save max contacts:', err);
    }
  };

  useEffect(() => {
    if (token) {
      // Small delay to ensure axios.defaults.headers are set in AuthContext
      const timer = setTimeout(() => {
        fetchDashboardData();
        fetchSfmcStats();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [token, fetchDashboardData, fetchSfmcStats]);

  const handleAccountChange = (e) => {
    const newAccountId = e.target.value;
    setSelectedAccountId(newAccountId);
    fetchSfmcStats(newAccountId);
  };

  const getStatusClass = (status) => {
    switch (status) {
      case 'healthy': return 'healthy';
      case 'warning': return 'warning';
      case 'error': return 'error';
      default: return 'unknown';
    }
  };

  const handleClearDashboard = async () => {
    if (!window.confirm('Are you sure you want to clear all check results? This will delete all historical data.')) {
      return;
    }

    try {
      setError('');
      setSuccess('');
      const response = await axios.delete('/api/dashboard/clear');
      setSuccess(`Dashboard cleared successfully. Deleted ${response.data.deletedCount} check results.`);
      setTimeout(() => setSuccess(''), 5000);
      await fetchDashboardData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to clear dashboard');
      setTimeout(() => setError(''), 5000);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  // Navigate to monitoring configs filtered by status
  const handleStatusClick = (status) => {
    navigate(`/monitoring?status=${status}`);
  };

  if (loading) {
    return <div className="loading">Loading dashboard...</div>;
  }

  if (error) {
    return (
      <div style={{ padding: '20px' }}>
        <div style={{
          padding: '12px',
          background: '#fecaca',
          color: '#991b1b',
          borderRadius: '6px'
        }}>
          {error}
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="content-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h1 style={{ margin: '0 0 5px 0' }}>{sfmcStats?.currentAccount?.name || 'Dashboard'}</h1>
          <p style={{ margin: 0, color: '#6b7280' }}>
            Monitor your SFMC infrastructure health
          </p>
          <Link to="/billing" style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            padding: '6px 12px',
            marginTop: '8px',
            background: darkMode ? '#1e3a5f' : '#dbeafe',
            border: `2px solid ${darkMode ? '#3b82f6' : '#2563eb'}`,
            borderRadius: '6px',
            textDecoration: 'none',
            color: darkMode ? '#60a5fa' : '#2563eb',
            fontSize: '13px',
            fontWeight: '600'
          }}>
            {(() => {
              const planMap = { 'pro': 'Starter', 'gold': 'Professional', 'platinum': 'Enterprise', 'starter': 'Starter', 'growth': 'Professional', 'enterprise': 'Enterprise', 'free': 'Free' };
              return planMap[summary?.planLimits?.plan?.toLowerCase()] || summary?.planLimits?.plan || 'Free';
            })()} Plan ↗
          </Link>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 }}>
          <select
            value={selectedAccountId}
            onChange={handleAccountChange}
            disabled={!sfmcStats?.accounts || sfmcStats.accounts.length === 0}
            style={{
              padding: '10px 16px',
              borderRadius: '8px',
              border: '2px solid #3b82f6',
              fontSize: '18px',
              fontWeight: '700',
              background: darkMode ? '#1e3a5f' : '#dbeafe',
              color: darkMode ? '#fff' : '#1e40af',
              cursor: sfmcStats?.accounts?.length > 0 ? 'pointer' : 'not-allowed',
              minWidth: '160px'
            }}
          >
            {sfmcStats?.accounts?.length > 0 ? (
              sfmcStats.accounts.map(account => (
                <option key={account.id} value={account.id}>
                  {account.account_mid || account.account_name}
                </option>
              ))
            ) : (
              <option value="">Loading...</option>
            )}
          </select>
          <button
            className="btn btn-danger"
            onClick={handleClearDashboard}
          >
            Clear Dashboard
          </button>
          <button
            className="btn btn-primary"
            onClick={fetchDashboardData}
          >
            Refresh
          </button>
        </div>
      </div>

      <div className="content-body">
        {error && (
          <div style={{
            padding: '12px',
            background: '#fecaca',
            color: '#991b1b',
            borderRadius: '6px',
            marginBottom: '20px'
          }}>
            {error}
          </div>
        )}

        {success && (
          <div style={{
            padding: '12px',
            background: '#d1fae5',
            color: '#065f46',
            borderRadius: '6px',
            marginBottom: '20px'
          }}>
            {success}
          </div>
        )}


      {/* Quota Usage */}
      {summary?.planLimits && (
        <div className="card" style={{ marginBottom: "20px" }}>
          <div className="card-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <h3>Quota Usage <span style={{ marginLeft: "8px", padding: "2px 8px", backgroundColor: "#3b82f6", color: "white", fontSize: "11px", fontWeight: "600", borderRadius: "4px" }}>NEW</span></h3>
            <span style={{ fontSize: "12px", textTransform: "uppercase", fontWeight: "600", color: darkMode ? "#9ca3af" : "#6b7280" }}>
              {summary.planLimits.plan} PLAN
            </span>
          </div>
          <div className="card-body">
            <div style={{ display: "flex", justifyContent: "space-around", flexWrap: "wrap", gap: "20px" }}>
              <Speedometer
                value={summary.activeConfigs || 0}
                max={summary.planLimits.maxConfigs || 1}
                label="Monitoring Configurations"
              />
              <Speedometer
                value={summary.totalAccounts || 0}
                max={summary.planLimits.maxBusinessUnits === 999 ? summary.totalAccounts + 10 : summary.planLimits.maxBusinessUnits || 1}
                label="Business Units"
              />
              <Speedometer
                  value={sfmcStats?.totalContacts || 0}
                  max={summary.planLimits.maxContacts || 1}
                  label="Contacts"
                  disabled={!summary.planLimits.maxContacts}
                />
            </div>
          </div>
        </div>
      )}

        {/* Monitoring Status Section */}
        <div className="card" style={{ marginBottom: '20px' }}>
          <div className="card-header" style={{ background: darkMode ? '#064e3b' : '#d1fae5', borderBottom: `2px solid ${darkMode ? '#10b981' : '#059669'}` }}>
            <h3 style={{ margin: 0, color: darkMode ? '#a7f3d0' : '#065f46' }}>Monitoring Status</h3>
          </div>
          <div className="card-body">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '15px' }}>
              <div style={{ textAlign: 'center', padding: '15px', background: darkMode ? '#374151' : '#f3f4f6', borderRadius: '8px', border: `2px solid ${darkMode ? '#6b7280' : '#d1d5db'}` }}>
                <div style={{ fontSize: '14px', color: darkMode ? '#9ca3af' : '#4b5563', marginBottom: '8px', fontWeight: '500' }}>Configs</div>
                <div style={{ fontSize: '28px', fontWeight: '700', color: darkMode ? '#fff' : '#111827' }}>
                  {summary?.activeConfigs || 0}
                  <span style={{ fontSize: '14px', fontWeight: '400', color: darkMode ? '#9ca3af' : '#6b7280' }}> / {summary?.pausedConfigs || 0} / {summary?.totalConfigs || 0}</span>
                </div>
                <div style={{ fontSize: '11px', color: darkMode ? '#9ca3af' : '#6b7280', marginTop: '4px' }}>active / paused / total</div>
              </div>
              <div style={{ textAlign: 'center', padding: '15px', background: darkMode ? '#065f46' : '#d1fae5', borderRadius: '8px', border: `2px solid ${darkMode ? '#10b981' : '#059669'}` }}>
                <div style={{ fontSize: '14px', color: darkMode ? '#a7f3d0' : '#065f46', marginBottom: '8px', fontWeight: '500' }}>Healthy</div>
                <div style={{ fontSize: '28px', fontWeight: '700', color: darkMode ? '#fff' : '#065f46' }}>
                  {summary?.statusCounts?.healthy || 0}
                </div>
              </div>
              <div
                onClick={() => (summary?.statusCounts?.warning || 0) > 0 && handleStatusClick('warning')}
                style={{
                  textAlign: 'center',
                  padding: '15px',
                  background: darkMode ? '#92400e' : '#fef3c7',
                  borderRadius: '8px',
                  border: `2px solid ${darkMode ? '#f59e0b' : '#d97706'}`,
                  cursor: (summary?.statusCounts?.warning || 0) > 0 ? 'pointer' : 'default',
                  transition: 'transform 0.1s, box-shadow 0.1s'
                }}
                onMouseEnter={(e) => {
                  if ((summary?.statusCounts?.warning || 0) > 0) {
                    e.currentTarget.style.transform = 'scale(1.02)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(245, 158, 11, 0.3)';
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'scale(1)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <div style={{ fontSize: '14px', color: darkMode ? '#fde68a' : '#92400e', marginBottom: '8px', fontWeight: '500' }}>Warnings</div>
                <div style={{ fontSize: '28px', fontWeight: '700', color: darkMode ? '#fff' : '#92400e' }}>
                  {summary?.statusCounts?.warning || 0}
                </div>
                {(summary?.statusCounts?.warning || 0) > 0 && (
                  <div style={{ fontSize: '11px', color: darkMode ? '#fcd34d' : '#b45309', marginTop: '4px' }}>
                    Click to view
                  </div>
                )}
              </div>
              <div
                onClick={() => (summary?.statusCounts?.error || 0) > 0 && handleStatusClick('error')}
                style={{
                  textAlign: 'center',
                  padding: '15px',
                  background: darkMode ? '#991b1b' : '#fee2e2',
                  borderRadius: '8px',
                  border: `2px solid ${darkMode ? '#ef4444' : '#dc2626'}`,
                  cursor: (summary?.statusCounts?.error || 0) > 0 ? 'pointer' : 'default',
                  transition: 'transform 0.1s, box-shadow 0.1s'
                }}
                onMouseEnter={(e) => {
                  if ((summary?.statusCounts?.error || 0) > 0) {
                    e.currentTarget.style.transform = 'scale(1.02)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(239, 68, 68, 0.3)';
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'scale(1)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <div style={{ fontSize: '14px', color: darkMode ? '#fecaca' : '#991b1b', marginBottom: '8px', fontWeight: '500' }}>Errors</div>
                <div style={{ fontSize: '28px', fontWeight: '700', color: darkMode ? '#fff' : '#991b1b' }}>
                  {summary?.statusCounts?.error || 0}
                </div>
                {(summary?.statusCounts?.error || 0) > 0 && (
                  <div style={{ fontSize: '11px', color: darkMode ? '#fca5a5' : '#b91c1c', marginTop: '4px' }}>
                    Click to view
                  </div>
                )}
              </div>
            </div>
            <div style={{ marginTop: '20px', paddingTop: '15px', borderTop: `1px solid ${darkMode ? '#4b5563' : '#e5e7eb'}` }}>
              <div style={{ fontSize: '14px', color: darkMode ? '#9ca3af' : '#4b5563', marginBottom: '10px', fontWeight: '500' }}>Configured Monitoring</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '15px' }}>
                <div style={{ textAlign: 'center', padding: '12px', background: darkMode ? '#374151' : '#f3f4f6', borderRadius: '6px' }}>
                  <div style={{ fontSize: '12px', color: darkMode ? '#9ca3af' : '#4b5563', marginBottom: '4px' }}>Automations Monitored</div>
                  <div style={{ fontSize: '20px', fontWeight: '600', color: darkMode ? '#fff' : '#111827' }}>
                    {sfmcStats?.configuredAutomations || 0}
                  </div>
                </div>
                <div style={{ textAlign: 'center', padding: '12px', background: darkMode ? '#374151' : '#f3f4f6', borderRadius: '6px' }}>
                  <div style={{ fontSize: '12px', color: darkMode ? '#9ca3af' : '#4b5563', marginBottom: '4px' }}>Data Extensions Monitored</div>
                  <div style={{ fontSize: '20px', fontWeight: '600', color: darkMode ? '#fff' : '#111827' }}>
                    {sfmcStats?.configuredDataExtensions || 0}
                  </div>
                </div>
                <div style={{ textAlign: 'center', padding: '12px', background: darkMode ? '#374151' : '#f3f4f6', borderRadius: '6px' }}>
                  <div style={{ fontSize: '12px', color: darkMode ? '#9ca3af' : '#4b5563', marginBottom: '4px' }}>Journeys Monitored</div>
                  <div style={{ fontSize: '20px', fontWeight: '600', color: darkMode ? '#fff' : '#111827' }}>
                    {sfmcStats?.configuredJourneys || 0}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Monitor Health (24h) Section */}
        <div className="card" style={{ marginBottom: '20px' }}>
          <div className="card-header" style={{ background: darkMode ? '#1a472a' : '#dcfce7', borderBottom: `2px solid ${darkMode ? '#16a34a' : '#22c55e'}` }}>
            <h3 style={{ margin: 0, color: darkMode ? '#86efac' : '#166534' }}>Monitor Health (24h)</h3>
          </div>
          <div className="card-body">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '15px' }}>
              <div style={{ textAlign: 'center', padding: '15px', background: darkMode ? '#1a472a' : '#dcfce7', borderRadius: '8px', border: `2px solid ${darkMode ? '#16a34a' : '#22c55e'}` }}>
                <div style={{ fontSize: '14px', color: darkMode ? '#86efac' : '#166534', marginBottom: '8px', fontWeight: '500' }}>Active Monitors</div>
                <div style={{ fontSize: '24px', fontWeight: '700', color: darkMode ? '#fff' : '#166534' }}>
                  {summary?.monitoringHealth?.activeMonitors ?? 0}
                </div>
              </div>
              <div style={{ textAlign: 'center', padding: '15px', background: darkMode ? '#1a472a' : '#dcfce7', borderRadius: '8px', border: `2px solid ${darkMode ? '#16a34a' : '#22c55e'}` }}>
                <div style={{ fontSize: '14px', color: darkMode ? '#86efac' : '#166534', marginBottom: '8px', fontWeight: '500' }}>Inactive Monitors</div>
                <div style={{ fontSize: '24px', fontWeight: '700', color: darkMode ? '#fff' : '#166534' }}>
                  {summary?.monitoringHealth?.inactiveMonitors ?? 0}
                </div>
              </div>
              <div style={{ textAlign: 'center', padding: '15px', background: darkMode ? '#1a472a' : '#dcfce7', borderRadius: '8px', border: `2px solid ${darkMode ? '#16a34a' : '#22c55e'}` }}>
                <div style={{ fontSize: '14px', color: darkMode ? '#86efac' : '#166534', marginBottom: '8px', fontWeight: '500' }}>Total Checks</div>
                <div style={{ fontSize: '24px', fontWeight: '700', color: darkMode ? '#fff' : '#166534' }}>
                  {summary?.monitoringHealth?.totalChecks24h ?? 0}
                </div>
              </div>
              <div style={{ textAlign: 'center', padding: '15px', background: darkMode ? '#1a472a' : '#dcfce7', borderRadius: '8px', border: `2px solid ${darkMode ? '#16a34a' : '#22c55e'}` }}>
                <div style={{ fontSize: '14px', color: darkMode ? '#86efac' : '#166534', marginBottom: '8px', fontWeight: '500' }}>Failed Checks</div>
                <div style={{ fontSize: '24px', fontWeight: '700', color: darkMode ? '#fff' : '#166534' }}>
                  {summary?.monitoringHealth?.failedChecks24h ?? 0}
                </div>
              </div>
              <div style={{ textAlign: 'center', padding: '15px', background: darkMode ? '#1a472a' : '#dcfce7', borderRadius: '8px', border: `2px solid ${darkMode ? '#16a34a' : '#22c55e'}` }}>
                <div style={{ fontSize: '14px', color: darkMode ? '#86efac' : '#166534', marginBottom: '8px', fontWeight: '500' }}>Warning Checks</div>
                <div style={{ fontSize: '24px', fontWeight: '700', color: darkMode ? '#fff' : '#166534' }}>
                  {summary?.monitoringHealth?.warningChecks24h ?? 0}
                </div>
              </div>
              <div style={{ textAlign: 'center', padding: '15px', background: darkMode ? '#1a472a' : '#dcfce7', borderRadius: '8px', border: `2px solid ${darkMode ? '#16a34a' : '#22c55e'}` }}>
                <div style={{ fontSize: '14px', color: darkMode ? '#86efac' : '#166534', marginBottom: '8px', fontWeight: '500' }}>Avg Response Time</div>
                <div style={{ fontSize: '24px', fontWeight: '700', color: darkMode ? '#fff' : '#166534' }}>
                  {summary?.monitoringHealth?.avgResponseTime !== null && summary?.monitoringHealth?.avgResponseTime !== undefined ? (
                    `${summary.monitoringHealth.avgResponseTime}ms`
                  ) : (
                    <span style={{ fontSize: '14px', color: darkMode ? '#9ca3af' : '#6b7280' }}>N/A</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* SFMC Instance Data Section */}

        {/* Health Score Widget */}
        {summary?.accounts && summary.accounts.some(a => a.health_score != null) && (
          <div className="card" style={{ marginBottom: '20px' }}>
            <div className="card-header" style={{ background: darkMode ? '#1e3a5f' : '#dbeafe', borderBottom: `2px solid ${darkMode ? '#3b82f6' : '#60a5fa'}` }}>
              <h3 style={{ margin: 0, color: darkMode ? '#93c5fd' : '#1e40af', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span>Operations Health Score</span>
                <a href="/audit" style={{ fontSize: '12px', color: darkMode ? '#60a5fa' : '#3b82f6', textDecoration: 'none' }}>Run Full Audit →</a>
              </h3>
            </div>
            <div className="card-body">
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px' }}>
                {summary.accounts.filter(a => a.health_score != null).map(account => {
                  const score = account.health_score;
                  const color = score >= 80 ? '#28a745' : score >= 60 ? '#ffc107' : score >= 40 ? '#fd7e14' : '#dc3545';
                  return (
                    <div key={account.id} style={{ textAlign: 'center', padding: '20px', borderRadius: '8px', border: `2px solid ${color}30`, background: `${color}10` }}>
                      <div style={{ fontSize: '12px', color: darkMode ? '#9ca3af' : '#6b7280', marginBottom: '8px' }}>{account.account_name}</div>
                      <div style={{ position: 'relative', width: '80px', height: '80px', margin: '0 auto 8px' }}>
                        <svg viewBox="0 0 80 80" style={{ width: '80px', height: '80px' }}>
                          <circle cx="40" cy="40" r="34" fill="none" stroke={darkMode ? '#374151' : '#e5e7eb'} strokeWidth="6" />
                          <circle cx="40" cy="40" r="34" fill="none" stroke={color} strokeWidth="6" strokeLinecap="round" strokeDasharray={`${(score / 100) * 214} 214`} transform="rotate(-90 40 40)" />
                        </svg>
                        <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', fontSize: '22px', fontWeight: 'bold', color: color }}>{score}</div>
                      </div>
                      <div style={{ fontSize: '11px', color: darkMode ? '#6b7280' : '#9ca3af' }}>
                        {account.health_score_updated_at ? new Date(account.health_score_updated_at).toLocaleDateString() : ''}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        <div className="card" style={{ marginBottom: '20px' }}>
          <div className="card-header" style={{ background: darkMode ? '#1e3a5f' : '#dbeafe', borderBottom: `2px solid ${darkMode ? '#3b82f6' : '#2563eb'}` }}>
            <h3 style={{ margin: 0, color: darkMode ? '#93c5fd' : '#1e40af' }}>SFMC Instance Data</h3>
          </div>
          <div className="card-body">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '15px' }}>
              <div
                style={{ textAlign: 'center', padding: '15px', background: darkMode ? '#1e3a5f' : '#dbeafe', borderRadius: '8px', border: `2px solid ${darkMode ? '#3b82f6' : '#2563eb'}`, cursor: 'pointer' }}
                onClick={() => {
                  if (!editingMaxContacts) {
                    setMaxContactsValue(summary?.planLimits?.maxContacts ? summary.planLimits.maxContacts.toLocaleString() : '');
                    setEditingMaxContacts(true);
                  }
                }}
              >
                <div style={{ fontSize: '14px', color: darkMode ? '#93c5fd' : '#1e40af', marginBottom: '8px', fontWeight: '500' }}>Max Contacts</div>
                <div style={{ fontSize: '24px', fontWeight: '700', color: darkMode ? '#fff' : '#1e40af' }}>
                  {editingMaxContacts ? (
                    <input
                      type="text"
                      value={maxContactsValue}
                      onChange={(e) => setMaxContactsValue(e.target.value)}
                      onBlur={() => {
                        setEditingMaxContacts(false);
                        saveMaxContacts(maxContactsValue);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          setEditingMaxContacts(false);
                          saveMaxContacts(maxContactsValue);
                        }
                        if (e.key === 'Escape') {
                          setEditingMaxContacts(false);
                        }
                      }}
                      autoFocus
                      style={{
                        width: '100%',
                        fontSize: '24px',
                        fontWeight: '700',
                        textAlign: 'center',
                        background: 'transparent',
                        border: 'none',
                        borderBottom: '2px solid #3b82f6',
                        color: darkMode ? '#fff' : '#1e40af',
                        outline: 'none'
                      }}
                      onClick={(e) => e.stopPropagation()}
                    />
                  ) : summary?.planLimits?.maxContacts ? (
                    <span style={{ borderBottom: '2px dashed #3b82f6', paddingBottom: '2px' }}>{summary.planLimits.maxContacts.toLocaleString()}</span>
                  ) : (
                    <span style={{ fontSize: '14px', color: darkMode ? '#9ca3af' : '#6b7280', borderBottom: '2px dashed #3b82f6', paddingBottom: '2px' }}>Click to set</span>
                  )}
                </div>
              </div>
              <div style={{ textAlign: 'center', padding: '15px', background: darkMode ? '#1e3a5f' : '#dbeafe', borderRadius: '8px', border: `2px solid ${darkMode ? '#3b82f6' : '#2563eb'}` }}>
                <div style={{ fontSize: '14px', color: darkMode ? '#93c5fd' : '#1e40af', marginBottom: '8px', fontWeight: '500' }}>Total Contacts</div>
                <div style={{ fontSize: '24px', fontWeight: '700', color: darkMode ? '#fff' : '#1e40af' }}>
                  {loadingSfmcStats ? (
                    <span style={{ fontSize: '14px', color: darkMode ? '#9ca3af' : '#6b7280' }}>Loading...</span>
                  ) : sfmcStats?.totalContacts !== null && sfmcStats?.totalContacts !== undefined ? (
                    sfmcStats.totalContacts.toLocaleString()
                  ) : (
                    <span style={{ fontSize: '14px', color: darkMode ? '#9ca3af' : '#6b7280' }}>N/A</span>
                  )}
                </div>
                {sfmcStats?.contactsError && (
                  <div style={{ fontSize: '11px', color: '#fca5a5', marginTop: '4px' }}>
                    {sfmcStats.contactsError}
                  </div>
                )}
              </div>
              <div style={{ textAlign: 'center', padding: '15px', background: darkMode ? '#1e3a5f' : '#dbeafe', borderRadius: '8px', border: `2px solid ${darkMode ? '#3b82f6' : '#2563eb'}` }}>
                <div style={{ fontSize: '14px', color: darkMode ? '#93c5fd' : '#1e40af', marginBottom: '8px', fontWeight: '500' }}>Automations</div>
                <div style={{ fontSize: '24px', fontWeight: '700', color: darkMode ? '#fff' : '#1e40af' }}>
                  {loadingSfmcStats ? (
                    <span style={{ fontSize: '14px', color: darkMode ? '#9ca3af' : '#6b7280' }}>Loading...</span>
                  ) : sfmcStats?.automationCount !== null && sfmcStats?.automationCount !== undefined ? (
                    sfmcStats.automationCount.toLocaleString()
                  ) : (
                    <span style={{ fontSize: '14px', color: darkMode ? '#9ca3af' : '#6b7280' }}>N/A</span>
                  )}
                </div>
                {sfmcStats?.automationError && (
                  <div style={{ fontSize: '11px', color: '#fca5a5', marginTop: '4px' }}>
                    {sfmcStats.automationError}
                  </div>
                )}
              </div>
              <div style={{ textAlign: 'center', padding: '15px', background: darkMode ? '#1e3a5f' : '#dbeafe', borderRadius: '8px', border: `2px solid ${darkMode ? '#3b82f6' : '#2563eb'}` }}>
                <div style={{ fontSize: '14px', color: darkMode ? '#93c5fd' : '#1e40af', marginBottom: '8px', fontWeight: '500' }}>Data Extensions</div>
                <div style={{ fontSize: '24px', fontWeight: '700', color: darkMode ? '#fff' : '#1e40af' }}>
                  {loadingSfmcStats ? (
                    <span style={{ fontSize: '14px', color: darkMode ? '#9ca3af' : '#6b7280' }}>Loading...</span>
                  ) : sfmcStats?.dataExtensionCount !== null && sfmcStats?.dataExtensionCount !== undefined ? (
                    sfmcStats.dataExtensionCount.toLocaleString()
                  ) : (
                    <span style={{ fontSize: '14px', color: darkMode ? '#9ca3af' : '#6b7280' }}>N/A</span>
                  )}
                </div>
                {sfmcStats?.dataExtensionError && (
                  <div style={{ fontSize: '11px', color: '#fca5a5', marginTop: '4px' }}>
                    {sfmcStats.dataExtensionError}
                  </div>
                )}
              </div>
              <div style={{ textAlign: 'center', padding: '15px', background: darkMode ? '#1e3a5f' : '#dbeafe', borderRadius: '8px', border: `2px solid ${darkMode ? '#3b82f6' : '#2563eb'}` }}>
                <div style={{ fontSize: '14px', color: darkMode ? '#93c5fd' : '#1e40af', marginBottom: '8px', fontWeight: '500' }}>Journeys (Regular)</div>
                <div style={{ fontSize: '24px', fontWeight: '700', color: darkMode ? '#fff' : '#1e40af' }}>
                  {loadingSfmcStats ? (
                    <span style={{ fontSize: '14px', color: darkMode ? '#9ca3af' : '#6b7280' }}>Loading...</span>
                  ) : sfmcStats?.journeyRegularCount !== null && sfmcStats?.journeyRegularCount !== undefined ? (
                    sfmcStats.journeyRegularCount.toLocaleString()
                  ) : (
                    <span style={{ fontSize: '14px', color: darkMode ? '#9ca3af' : '#6b7280' }}>N/A</span>
                  )}
                </div>
                {sfmcStats?.journeyError && (
                  <div style={{ fontSize: '11px', color: '#fca5a5', marginTop: '4px' }}>
                    {sfmcStats.journeyError}
                  </div>
                )}
              </div>
              <div style={{ textAlign: 'center', padding: '15px', background: darkMode ? '#1e3a5f' : '#dbeafe', borderRadius: '8px', border: `2px solid ${darkMode ? '#3b82f6' : '#2563eb'}` }}>
                <div style={{ fontSize: '14px', color: darkMode ? '#93c5fd' : '#1e40af', marginBottom: '8px', fontWeight: '500' }}>Journeys (Single Send)</div>
                <div style={{ fontSize: '24px', fontWeight: '700', color: darkMode ? '#fff' : '#1e40af' }}>
                  {loadingSfmcStats ? (
                    <span style={{ fontSize: '14px', color: darkMode ? '#9ca3af' : '#6b7280' }}>Loading...</span>
                  ) : sfmcStats?.journeySingleSendCount !== null && sfmcStats?.journeySingleSendCount !== undefined ? (
                    sfmcStats.journeySingleSendCount.toLocaleString()
                  ) : (
                    <span style={{ fontSize: '14px', color: darkMode ? '#9ca3af' : '#6b7280' }}>N/A</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Recent Sends Deliverability */}
        <div className="card">
          <div className="card-header">
            <h3>Recent Send Deliverability</h3>
          </div>
          <div className="card-body">
            {loadingSfmcStats ? (
              <div className="empty-state">
                <p>Loading deliverability data...</p>
              </div>
            ) : sfmcStats?.recentSends && sfmcStats.recentSends.length > 0 ? (
              <table className="table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Email Name</th>
                    <th>Sent</th>
                    <th>Delivered</th>
                    <th>Bounced</th>
                    <th>Delivery Rate</th>
                  </tr>
                </thead>
                <tbody>
                  {sfmcStats.recentSends.map((send) => (
                    <tr key={send.id}>
                      <td>{formatDate(send.sentDate)}</td>
                      <td>{send.emailName}</td>
                      <td>{send.numberSent.toLocaleString()}</td>
                      <td>{send.numberDelivered.toLocaleString()}</td>
                      <td>{send.numberBounced.toLocaleString()}</td>
                      <td>
                        <span style={{
                          color: send.deliveryRate >= 95 ? '#16a34a' :
                                 send.deliveryRate >= 90 ? '#ea580c' : '#dc2626'
                        }}>
                          {send.deliveryRate}%
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="empty-state">
                <h3>No Recent Sends</h3>
                <p>{sfmcStats?.sendsError || 'No send data available from SFMC'}</p>
              </div>
            )}
          </div>
        </div>

        {/* Recent Checks */}
        <div className="card">
          <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h3>Recent Checks</h3>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                className={`btn ${checksFilter === 'all' ? 'btn-primary' : 'btn-secondary'}`}
                style={{ padding: '4px 12px', fontSize: '12px' }}
                onClick={() => setChecksFilter('all')}
              >
                All
              </button>
              <button
                className={`btn ${checksFilter === 'errors' ? 'btn-danger' : 'btn-secondary'}`}
                style={{ padding: '4px 12px', fontSize: '12px' }}
                onClick={() => setChecksFilter('errors')}
              >
                Errors Only
              </button>
              <button
                className="btn btn-secondary"
                style={{ padding: '4px 12px', fontSize: '12px' }}
                onClick={handleClearDashboard}
              >
                Clear Log
              </button>
            </div>
          </div>
          <div className="card-body">
            {(() => {
              const filteredChecks = checksFilter === 'errors'
                ? recentChecks.filter(c => c.status === 'error')
                : recentChecks;

              if (filteredChecks.length === 0) {
                return (
                  <div className="empty-state">
                    <h3>{checksFilter === 'errors' ? 'No Recent Errors' : 'No Recent Checks'}</h3>
                    <p>{checksFilter === 'errors' ? 'All systems are running smoothly' : 'No checks have been performed yet'}</p>
                  </div>
                );
              }

              return (
                <table className="table">
                  <thead>
                    <tr>
                      <th>Time</th>
                      <th>Config</th>
                      <th>Type</th>
                      <th>Status</th>
                      <th>Message</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredChecks.map((check) => (
                      <tr key={check.id}>
                        <td>{formatDate(check.checked_at)}</td>
                        <td>{check.config_name}</td>
                        <td>
                          <span style={{ textTransform: 'capitalize' }}>
                            {check.object_type}
                          </span>
                        </td>
                        <td>
                          <span className={`status-badge ${getStatusClass(check.status)}`}>
                            {check.status}
                          </span>
                        </td>
                        <td>
                          {check.message || 'Check completed'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              );
            })()}
          </div>
        </div>

        {/* SFMC Accounts Status */}
        {summary?.accounts && summary.accounts.length > 0 && (
          <div className="card">
            <div className="card-header">
              <h3>SFMC Accounts</h3>
            </div>
            <div className="card-body">
              <table className="table">
                <thead>
                  <tr>
                    <th>Account Name</th>
                    <th>Active Configs</th>
                    <th>Paused Configs</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {summary.accounts.map((account) => (
                    <tr key={account.id}>
                      <td>{account.account_name}</td>
                      <td>{account.active_count || 0}</td>
                      <td>{account.paused_count || 0}</td>
                      <td>
                        <span className={`status-badge ${account.is_active ? 'healthy' : 'unknown'}`}>
                          {account.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Dashboard;
